"use client";

import { Alert, Button } from "@zapier/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { clientEnv } from "lib/env";
import {
  useUrlParameters,
  useFacebookSdk,
  useWabaEmbeddedSignupEvents,
  useLaunchWabaSignup,
  useWabaAuthorization,
} from "../hooks";
import { safeTrack } from "../observability/tracking/safeTrack";
import { STORAGE_KEYS, getSessionItem } from "../utils/storageKeys";
import {
  getUserFacingErrorMessage,
  type ErrorDetails,
} from "../utils/errorMessages";
import isBrowser from "../utils/isBrowser";

function renderErrorAlert(errorDetails: ErrorDetails) {
  return (
    <Alert status="error" variant="toast">
      <div>
        <div style={{ fontWeight: "600", marginBottom: "8px" }}>
          {errorDetails.message}
        </div>
        <div style={{ fontSize: "14px", marginBottom: "8px" }}>
          {errorDetails.helpText}
        </div>
        {errorDetails.helpLink && (
          <a
            href={errorDetails.helpLink}
            style={{
              textDecoration: "underline",
              fontWeight: "bold",
              fontSize: "14px",
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Log in to Zapier →
          </a>
        )}
      </div>
    </Alert>
  );
}

function renderDebugInfo(wabaId: string | null) {
  if (process.env.NODE_ENV !== "development" || !wabaId) {
    return null;
  }

  return (
    <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
      WABA ID cached: {wabaId}
    </div>
  );
}

export function WabaSignupModal() {
  const urlParams = useUrlParameters();
  const {
    appId: urlAppId,
    configId: urlConfigId,
    callbackUrl,
    cliSessionId,
    state,
  } = urlParams;
  const appId = urlAppId || clientEnv.NEXT_PUBLIC_FACEBOOK_APP_ID;
  const configId = urlConfigId || clientEnv.NEXT_PUBLIC_FACEBOOK_CONFIG_ID;

  // Comprehensive authorization check (authentication + referrer + params)
  const authorization = useWabaAuthorization(urlParams);

  // Facebook SDK lifecycle
  const {
    ready: sdkReady,
    loading: sdkLoading,
    error: sdkError,
  } = useFacebookSdk({ appId });

  // Embedded signup events
  const { error: eventError } = useWabaEmbeddedSignupEvents();

  // Launch logic
  const {
    launch,
    loading: launchLoading,
    error: launchError,
  } = useLaunchWabaSignup({
    appId,
    configId,
    callbackUrl,
    cliSessionId,
    state,
    sdkReady,
  });

  const combinedError = useMemo(
    () => launchError || eventError || sdkError,
    [launchError, eventError, sdkError],
  );
  const isDisabled = useMemo(
    () =>
      !authorization.isAuthorized ||
      launchLoading ||
      sdkLoading ||
      !configId ||
      !appId ||
      !sdkReady,
    [
      authorization.isAuthorized,
      launchLoading,
      sdkLoading,
      configId,
      appId,
      sdkReady,
    ],
  );

  const errorContext = useMemo(
    () => ({
      hasAppId: !!appId,
      hasConfigId: !!configId,
      hasState: !!state,
      hasRedirectUri: !!callbackUrl,
    }),
    [appId, configId, state, callbackUrl],
  );

  const errorDetails = useMemo(() => {
    if (!combinedError) return null;
    return getUserFacingErrorMessage(combinedError, errorContext);
  }, [combinedError, errorContext]);

  // Track client-side mount to avoid hydration mismatch
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isBrowser()) {
      setIsMounted(true);
    }
  }, []);

  const disabledTooltip = useMemo(() => {
    // Only compute on client to avoid hydration mismatch
    if (!isMounted) {
      return "This page must be accessed with valid WhatsApp Business Account configuration parameters. Please log in to Zapier.";
    }

    if (!authorization.isAuthorized && authorization.errorDetails) {
      return authorization.errorDetails.message;
    }
    if (!appId || !configId) {
      return "Missing required configuration. Please access this page through Zapier.";
    }
    if (sdkLoading) {
      return "Loading Facebook SDK...";
    }
    if (!sdkReady) {
      return "Facebook SDK is not ready. Please wait or refresh the page.";
    }
    return undefined;
  }, [
    isMounted,
    authorization.isAuthorized,
    authorization.errorDetails,
    appId,
    configId,
    sdkLoading,
    sdkReady,
  ]);

  const handlePress = useCallback(() => {
    safeTrack("user_clicked_launch", {
      disabled: isDisabled,
      has_app_id: !!appId,
      has_config_id: !!configId,
      sdk_ready: sdkReady,
    });
    if (!isDisabled) {
      void launch();
    }
  }, [isDisabled, appId, configId, sdkReady, launch]);

  const persistedIds = useMemo(() => {
    return {
      wabaId: getSessionItem(STORAGE_KEYS.wabaId),
    };
  }, [eventError]);

  return (
    <div className="waba-signup-modal" aria-live="polite">
      {/* Show authorization errors with priority */}
      {authorization.errorDetails &&
        renderErrorAlert(authorization.errorDetails)}

      {/* Show SDK/launch errors if no authorization errors */}
      {!authorization.errorDetails &&
        errorDetails &&
        renderErrorAlert(errorDetails)}

      <span title={isDisabled ? disabledTooltip : undefined}>
        <Button
          onPress={handlePress}
          isDisabled={isDisabled}
          className="waba-signup-trigger"
          aria-busy={launchLoading || sdkLoading ? "true" : "false"}
          aria-label={
            launchLoading || sdkLoading
              ? "Connecting with Meta"
              : "Connect with Meta"
          }
        >
          <span className="waba-signup-trigger-inner">
            {!(launchLoading || sdkLoading) && (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            )}
            {launchLoading || sdkLoading
              ? "Connecting..."
              : "Connect with Meta"}
          </span>
        </Button>
      </span>

      {/* Optional debug info in dev only */}
      {renderDebugInfo(persistedIds.wabaId)}
    </div>
  );
}
